import type { FunctionComponent } from 'react'
import { useContext } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'
import {
  Accordion,
  AccordionItem,
  Button,
  LinkButton,
  QuestionGroup,
  TypographyV2 as Typography,
} from '@which/seatbelt'
import { PadlockIcon } from '@which/seatbelt/src/components/Icons/Miscellaneous'
import type { QuestionRowProps } from '@which/seatbelt/src/components/QuestionGroup/components/QuestionRow'
import type { FilterData } from '@which/shared'
import { dynamicDatalayerPush } from '@which/shared'

import classnames from 'classnames'
import FocusTrap from 'focus-trap-react'

import type {
  Filter as FilterType,
  NeedsBasedAnswer,
  NeedsBasedFilter,
} from '../../../../../generated/frontend'
import type { PageParams } from '../../../../../routes'
import { focusAboveCompareTray } from '../../../../../shared/components/ReviewsCompareTray'
import type { ProductCount } from '../../../../../shared/types/ReviewsFilters'
import { ReviewsTemplateContext } from '../../ReviewsTemplateContext'
import { getIconComponent } from '../../utils/icon-map'
import { Filter } from '../'
import { FilterSignupCTA } from '../FilterSignupCTA/FilterSignupCTA'
import { BADGES, TEST_RESULTS } from './constants/external-ids'
import styles from './Filters.module.scss'
import { FilterTooltip } from './FilterTooltip/FilterTooltip'
import { hasNoApplicableFilters } from './utils'

export const Filters: FunctionComponent<FiltersProps> = ({
  className,
  filters,
  productCount,
  needsBasedFilters,
}) => {
  const { categorySlug } = useParams<PageParams>()
  const {
    state: {
      filterContext: {
        basicFilterData,
        filterCallback,
        isOverlayFiltersDisplayed,
        setIsOverlayFiltersDisplayed,
        selectedNeedsBasedFilters,
      },
    },
  } = useContext(ReviewsTemplateContext)

  if (!filters?.length) {
    return null
  }

  const applyMobileFiltersHandler = () => {
    setIsOverlayFiltersDisplayed && setIsOverlayFiltersDisplayed(false)
  }

  const cancelMobileFiltersHandler = () => {
    setIsOverlayFiltersDisplayed && setIsOverlayFiltersDisplayed(false)
    filterCallback({ revertToSnapshot: true })
  }

  const clearAllFiltersHandler = () => {
    filterCallback({ reset: true })
    dynamicDatalayerPush({
      eventAction: 'clear filters',
      eventCategory: 'refine results',
      eventLabel: 'clear all filters',
    })
  }

  const needsBasedFilterEventHandler = ({
    needsBasedFilter,
    answer,
  }: {
    needsBasedFilter: NeedsBasedFilter
    answer: NeedsBasedAnswer
  }) => {
    const { questionText } = needsBasedFilter
    const { label, needsBasedAnswerOptions } = answer

    filterCallback({
      questionText,
      options: needsBasedAnswerOptions,
      nbfLabel: label,
      individualFilters: filters,
      isNbfSelected: selectedNeedsBasedFilters?.[questionText]?.[label],
    })

    dynamicDatalayerPush({
      eventAction: `${categorySlug} - smart recs - ${questionText}`,
      eventCategory: 'refine results',
      eventLabel: label,
    })
  }

  const questions = needsBasedFilters.reduce(
    (questionsCollection: QuestionRowProps[], needsBasedFilter) => {
      if (!needsBasedFilter.disabled) {
        questionsCollection.push({
          ...needsBasedFilter,
          answers: needsBasedFilter.answers.map((answer, index) => ({
            ...answer,
            answerIndex: index,
            questionText: needsBasedFilter.questionText,
            totalAnswers: needsBasedFilter.answers.length,
            selected: selectedNeedsBasedFilters?.[needsBasedFilter.questionText]?.[answer.label],
            icon: getIconComponent(answer.icon),
            value: answer.label,
            description: answer.caption,
            onClickCallback: () => needsBasedFilterEventHandler({ needsBasedFilter, answer }),
          })),
        })
      }

      return questionsCollection
    },
    []
  )

  const filtersJsx = (
    <section
      className={classnames(
        className,
        styles.wrapper,
        isOverlayFiltersDisplayed && styles.displayAsOverlay
      )}
      data-testid="product-filters"
      onFocus={focusAboveCompareTray}
    >
      <div className={styles.header}>
        <Typography data-testid="filters-header" tag="h2" textStyle="sb-text-body-default-regular">
          Filters
        </Typography>
        <LinkButton className={styles.clearAllFilters} onClick={clearAllFiltersHandler}>
          Clear all
        </LinkButton>
      </div>
      {!!questions.length && (
        <QuestionGroup questions={questions} className={styles.questionGroup} />
      )}
      <Accordion tag="ul" className={styles.list}>
        {filters.map(
          (
            {
              filterLabel,
              filterItems,
              filterLabelTooltip,
              componentType,
              externalId,
              caption,
              disabled,
              exclusion,
            },
            index
          ) => {
            const hasAllCountsZero = hasNoApplicableFilters(filterItems) || !productCount.total

            const filter = (
              <Filter
                {...{
                  filterItems,
                  componentType,
                  filterLabel,
                  externalId,
                  exclusion,
                  basicFilterData,
                  disabled,
                }}
                filterCallback={filterCallback}
              />
            )

            const content = (
              <div className={styles.accordionContent}>
                {filterLabelTooltip && Object.keys(filterLabelTooltip).length > 0 && (
                  <FilterTooltip
                    categorySlug={categorySlug}
                    filterLabel={filterLabel}
                    title={filterLabelTooltip.label || ''}
                    description={filterLabelTooltip.description || ''}
                    displayType="block"
                  />
                )}
                {[BADGES, TEST_RESULTS].includes(externalId) && caption && (
                  <Typography
                    className="sb-margin-bottom-2"
                    data-testid="caption"
                    textStyle="sb-text-interface-body-small-regular"
                  >
                    {caption}
                  </Typography>
                )}
                {componentType === 'checkbox' ? (
                  <div className={styles.filterWrapper}>{filter}</div>
                ) : (
                  filter
                )}
                {disabled && externalId === BADGES && <FilterSignupCTA />}
              </div>
            )

            const accordionItemProps = {
              className: styles.accordionOverride,
              animatesOpen: false,
              key: `${filterLabel}-${index}`,
              ariaLabel: filterLabel,
              label: (
                <>
                  {disabled && <PadlockIcon className={styles.padlockIcon} data-testid="padlock" />}
                  {filterLabel}
                </>
              ),
              renderOpen: true,
              callback: (open: boolean) =>
                dynamicDatalayerPush({
                  eventLabel: `${categorySlug} - ${filterLabel}`,
                  eventCategory: 'Product Listing',
                  eventAction: `Accordion - ${open ? 'Expand' : 'Collapse'}`,
                }),
              content,
              tag: 'li' as const,
            }

            return (
              ((disabled && externalId === BADGES) || !hasAllCountsZero) && (
                <AccordionItem data-testid="accordion-item" {...accordionItemProps} />
              )
            )
          }
        )}
      </Accordion>

      <div className={styles.footer}>
        <Typography textStyle="sb-text-body-default-strong" className={styles.footerResults}>
          {productCount.total} Results
        </Typography>
        <div className={styles.footerButtonWrapper}>
          <Button
            className={styles.footerButton}
            appearance="secondary"
            onClick={cancelMobileFiltersHandler}
          >
            <Typography tag="span" textStyle="sb-text-body-default-strong">
              Cancel
            </Typography>
          </Button>
          <Button className={styles.footerButton} onClick={applyMobileFiltersHandler}>
            <Typography tag="span" textStyle="sb-text-body-default-strong">
              Apply
            </Typography>
          </Button>
        </div>
      </div>
    </section>
  )

  return isOverlayFiltersDisplayed ? <FocusTrap>{filtersJsx}</FocusTrap> : filtersJsx
}

///////IMPLEMENTATION/////////

export type FiltersProps = {
  className?: string
  filters: FilterType[]
  productCount: ProductCount
  needsBasedFilters: NeedsBasedFilter[]
}

export const getCleanedFilterData = (basicFilterData: FilterData) => {
  const clonedFilterData = structuredClone(basicFilterData)

  Object.keys(clonedFilterData).forEach((key) => {
    Object.keys(clonedFilterData[key]).forEach((innerKey) => {
      /* v8 ignore else */
      if (clonedFilterData[key][innerKey].length === 0) {
        delete clonedFilterData[key][innerKey]
      }
    })

    /* v8 ignore else */
    if (Object.keys(clonedFilterData[key]).length === 0) {
      delete clonedFilterData[key]
    }
  })

  return clonedFilterData
}
